import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button, Input, Form, Spin, Alert, Divider, Tag, Checkbox } from 'antd';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const AttendanceRegisterQR = () => {
  const { classId: paramClassId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authAxios } = useAuth();
  
  // URL 파라미터 또는 쿼리 스트링에서 classId 가져오기 (여기서는 실제로 classroomId)
  const classroomId = paramClassId || searchParams.get('classId');
  
  // QR 코드 토큰
  const token = searchParams.get('token');
  const type = searchParams.get('type') || 'checkin'; // 기본값은 입실
  
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [classroomInfo, setClassroomInfo] = useState(null);
  const [currentClasses, setCurrentClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  
  // 폼 상태
  const [form] = Form.useForm();
  
  // 현재 시간
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // QR 코드 검증 및 강의실 정보 로드
  const validateQRAndLoadClassroom = useCallback(async () => {
    if (!classroomId || !token) {
      setError('유효하지 않은 QR 코드입니다.');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // QR 코드 검증 API 호출
      const response = await axios.get(`/api/qr-gps/classes/${classroomId}/validate-qr?token=${token}`);
      
      if (response.data) {
        setClassroomInfo(response.data);
        // 강의실 ID로 진행 중인 강의 조회
        await loadCurrentClasses();
      } else {
        setError('유효하지 않은 QR 코드이거나 만료되었습니다.');
      }
    } catch (err) {
      console.error('QR 코드 검증 오류:', err);
      setError('QR 코드 검증 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [classroomId, token]);
  
  // 진행 중인 강의 조회
  const loadCurrentClasses = async () => {
    try {
      setLoadingClasses(true);
      console.log(`강의실 ID(${classroomId})로 진행 중인 강의 조회 중...`);
      
      // API 경로에 classroomId가 올바르게 포함되었는지 확인
      if (!classroomId) {
        console.error('강의실 ID가 없습니다.');
        setCurrentClasses([]);
        return;
      }
      
      const response = await axios.get(`/api/attendance/classroom/${classroomId}/current-classes`);
      console.log('강의실 진행 중인 강의 응답:', response.data);
      
      if (response.data.success) {
        // 응답 데이터가 배열인지 확인
        const classesData = Array.isArray(response.data.data) ? response.data.data : [];
        console.log(`${classesData.length}개의 강의가 조회됨`);
        
        // 강사 정보 디버깅
        classesData.forEach(cls => {
          console.log(`강의 [${cls.name}] 강사명: ${cls.instructorName || '미지정'}`);
        });
        
        setCurrentClasses(classesData);
        
        // 강의가 1개인 경우 자동 선택
        if (classesData.length === 1) {
          setSelectedClasses([classesData[0]]);
          console.log(`강의가 1개이므로 자동 선택됨: ${classesData[0].name}`);
        } else {
          setSelectedClasses([]); // 강의가 여러 개이면 선택 취소
        }
      } else {
        console.error('강의 조회 실패:', response.data.message);
        setCurrentClasses([]);
        toast.error('강의 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('진행 중인 강의 조회 오류:', err);
      console.error('오류 상세 정보:', err.response?.data);
      setCurrentClasses([]);
      toast.error('강의 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingClasses(false);
    }
  };
  
  // 위치 정보 가져오기
  const getGeoLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setGpsError(null);
        },
        (error) => {
          console.error('위치 정보 가져오기 오류:', error);
          setGpsError('위치 정보를 가져올 수 없습니다. 위치 권한을 허용해주세요.');
        },
        { enableHighAccuracy: true }
      );
    } else {
      setGpsError('이 브라우저는 위치 정보를 지원하지 않습니다.');
    }
  }, []);
  
  // 현재 시간 업데이트 (1초마다)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // 초기화
  useEffect(() => {
    validateQRAndLoadClassroom();
    getGeoLocation();
  }, [validateQRAndLoadClassroom, getGeoLocation]);
  
  // 출결 등록 제출 처리
  const handleSubmit = async (values) => {
    if (!gpsCoords && !gpsError) {
      toast.error('위치 정보를 가져오는 중입니다. 잠시만 기다려주세요.');
      return;
    }
    
    if (selectedClasses.length === 0) {
      toast.error('적어도 하나의 강의를 선택해주세요.');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // 선택된 모든 강의에 대해 출결 처리
      const promises = selectedClasses.map(async (cls) => {
        // 출결 데이터 구성
        const attendanceData = {
          classId: cls.id || cls._id, // 선택한 강의 ID
          classroomId: classroomId, // 강의실 ID
          token,
          type, // 입실/퇴실 구분
          studentName: values.name,
          studentPhone: values.phone,
          timestamp: new Date().toISOString(),
          ...(gpsCoords || {}) // GPS 좌표가 없으면 빈 객체로 대체
        };
        
        console.log(`${cls.name} 강의 출결 등록 요청 데이터:`, attendanceData);
        
        // 출결 등록 API 호출
        return axios.post('/api/attendance/register-by-qr', attendanceData);
      });
      
      // 모든 요청 처리 대기
      const results = await Promise.allSettled(promises);
      
      // 결과 처리
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;
      
      if (successful > 0) {
        toast.success(`${successful}개 강의의 ${type === 'checkin' ? '입실' : '퇴실'} 처리가 완료되었습니다.`);
      }
      
      if (failed > 0) {
        toast.error(`${failed}개 강의의 출결 등록에 실패했습니다.`);
      }
      
    } catch (err) {
      console.error('출결 등록 오류:', err);
      console.error('오류 상세 정보:', err.response?.data || err.message);
      console.error('오류 상태 코드:', err.response?.status);
      console.error('오류 헤더:', err.response?.headers);
      
      // 오류 메시지 개선
      let errorMessage = '출결 등록 중 오류가 발생했습니다.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };
  
  // 강의 선택 핸들러
  const handleClassSelect = (cls) => {
    const isSelected = selectedClasses.some(selectedCls => selectedCls._id === cls._id);
    
    if (isSelected) {
      // 이미 선택된 강의를 다시 클릭하면 선택 취소
      setSelectedClasses(selectedClasses.filter(selectedCls => selectedCls._id !== cls._id));
    } else {
      // 새로운 강의 선택 추가
      setSelectedClasses([...selectedClasses, cls]);
    }
  };

  // 선택된 강의인지 확인
  const isClassSelected = (cls) => {
    return selectedClasses.some(selectedCls => selectedCls._id === cls._id);
  };
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Spin size="large" />
        <p className="mt-4 text-gray-600">QR 코드 정보를 확인하는 중입니다...</p>
      </div>
    );
  }
  
  // 오류 표시
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Alert
          type="error"
          message="오류"
          description={error}
          showIcon
        />
        <Button 
          className="mt-4"
          onClick={() => navigate('/')}
        >
          홈으로 돌아가기
        </Button>
      </div>
    );
  }
  
  // 날짜 포맷팅 함수
  const formatDate = (date) => {
    if (!date) return '날짜 정보 없음';
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };
  
  // 시간 포맷팅 함수
  const formatTime = (date) => {
    if (!date) return '시간 정보 없음';
    return new Date(date).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };
  
  // 현재 진행 중인 강의 정보 렌더링
  const renderCurrentClasses = () => {
    if (loadingClasses) {
      return (
        <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-500 text-center">
          <Spin size="small" /> 강의 정보를 불러오는 중...
        </div>
      );
    }

    if (currentClasses.length === 0) {
      return (
        <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-500 text-center">
          오늘 강의실에서 예정된 강의가 없습니다.
        </div>
      );
    }

    return (
      <div className="mt-3">
        <Divider className="my-2">
          {currentClasses.length === 1 ? '오늘의 강의 (자동 선택됨)' : '강의를 선택해주세요 (중복 선택 가능)'}
        </Divider>
        <div className="space-y-2">
          {currentClasses.map((cls, index) => (
            <div 
              key={cls._id || index} 
              className={`p-2 border rounded cursor-pointer transition-colors ${
                isClassSelected(cls) 
                  ? 'bg-blue-100 border-blue-500' 
                  : 'bg-orange-50 border-orange-100 hover:bg-orange-100'
              }`}
              onClick={() => handleClassSelect(cls)}
            >
              <div className="font-medium text-orange-800 flex items-center">
                <Checkbox
                  checked={isClassSelected(cls)}
                  className="mr-2"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleClassSelect(cls)}
                />
                {cls.name}
              </div>
              <div className="text-xs text-gray-600 ml-6">
                <span>강사: {cls.instructorName || '미지정'}</span>
                {cls.category && (
                  <span className="ml-2">구분: {cls.category}</span>
                )}
                {cls.startTime && (
                  <span className="ml-2">시작: {new Date(cls.startTime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>
                )}
                {cls.endTime && (
                  <span className="ml-2">종료: {new Date(cls.endTime).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div className="flex flex-col items-center min-h-screen p-4 bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6 my-8">
        <h1 className="text-2xl font-bold text-center text-indigo-700 mb-6">
          강의실 {type === 'checkin' ? '입실' : '퇴실'} 등록
        </h1>
        
        {/* 강의실 정보 */}
        <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
          <h2 className="text-lg font-semibold text-indigo-800 mb-2">
            {classroomInfo?.name || '강의실 정보'}
          </h2>
          <p className="text-gray-600">
            <span className="font-medium">주소:</span> {classroomInfo?.location?.address || '정보 없음'}
          </p>
          <p className="text-gray-600">
            <span className="font-medium">날짜:</span> {formatDate(currentTime)}
          </p>
          <p className="text-gray-600">
            <span className="font-medium">시간:</span> {formatTime(currentTime)}
          </p>
          <p className="text-gray-600 mt-2 text-xs">
            {type === 'checkin' ? '입실' : '퇴실'} 정보는 강의실 위치와 시간을 기준으로 기록됩니다.
          </p>
          
          {/* 현재 진행 중인 강의 정보 표시 */}
          {renderCurrentClasses()}
        </div>
        
        {/* 선택된 강의 표시 */}
        {selectedClasses.length > 0 && (
          <div className="mb-4">
            <p className="font-medium mb-2">선택된 강의 {selectedClasses.length}개:</p>
            <div className="flex flex-wrap gap-2">
              {selectedClasses.map((cls, index) => (
                <Tag
                  key={index}
                  color="blue"
                  closable
                  onClose={(e) => {
                    e.preventDefault();
                    handleClassSelect(cls);
                  }}
                >
                  {cls.name}
                </Tag>
              ))}
            </div>
          </div>
        )}
        
        {/* GPS 상태 */}
        {gpsError ? (
          <Alert
            type="warning"
            message="위치 정보 오류"
            description={gpsError}
            showIcon
            className="mb-4"
            action={
              <Button size="small" onClick={getGeoLocation}>
                다시 시도
              </Button>
            }
          />
        ) : gpsCoords ? (
          <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
            위치 정보가 확인되었습니다.
          </div>
        ) : (
          <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700 flex items-center">
            <Spin size="small" className="mr-2" /> 위치 정보를 가져오는 중입니다...
          </div>
        )}
        
        {/* 출결 등록 폼 */}
        <Form 
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="이름"
            rules={[{ required: true, message: '이름을 입력해주세요.' }]}
          >
            <Input placeholder="홍길동" />
          </Form.Item>
          
          <Form.Item
            name="phone"
            label="연락처"
            rules={[
              { required: true, message: '연락처를 입력해주세요.' },
              { pattern: /^\d{3}-\d{3,4}-\d{4}$|^\d{2,3}-\d{3,4}-\d{4}$|^\d{10,11}$/, message: '유효한 전화번호 형식이 아닙니다.' }
            ]}
          >
            <Input placeholder="010-1234-5678" />
          </Form.Item>
          
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={loading || submitting || selectedClasses.length === 0}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              size="large"
            >
              {selectedClasses.length > 0 
                ? `${selectedClasses.length}개 강의 ${type === 'checkin' ? '입실' : '퇴실'} 등록하기` 
                : `강의를 선택해주세요`}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default AttendanceRegisterQR; 